// web-frontend/src/shared/services/authService.js
import apiClient, { setAuthToken, setRefreshToken, clearAuthTokens } from './apiClient';
import socialAuthService from './socialAuthService';
import { API_CONFIG } from '../constants/config';
import {
  storeToken,
  removeStoredToken,
  storeUserData,
  removeUserData,
  getStoredToken,
  getStoredUserData,
  isValidUUID,
} from '../utils/storage';
import deviceManager from '../utils/deviceManager';
import i18nService from './i18n'; // 🔥 [필수 추가] i18n 서비스 import
import logger from '../utils/logger';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.isAuthenticated = false;
    this.authStore = null; // 🔥 authStore 참조 추가
  }

  // 🔥 authStore 설정 (App.jsx에서 호출)
  setAuthStore(store) {
    this.authStore = store;
    logger.debug('✅ [AuthService] authStore 연결 완료');
  }

  // =========================================================================
  // 🔥 [핵심 수정] 로그인 시 언어 적용 로직 강화 (로컬 우선 + 서버 동기화)
  // =========================================================================
  async _applyUserLanguage(user) {
    if (!user) return;
    let finalLanguage = 'en'; // 기본값 (영어를 글로벌 기본 언어로 사용)
    
    // 1. 로컬 스토리지에 저장된 언어 (이전 세션에서 설정한 값)
    const localLang = typeof window !== 'undefined' ? localStorage.getItem('dsk_language') : null;
    
    // 2. 서버에서 받아온 언어
    const serverLang = user.preferred_language;

    logger.debug(`🌐 [AuthService] 언어 결정 - 서버: ${serverLang}, 로컬: ${localLang}`);

    // 🔥 [결정 로직] 
    // 로컬 설정이 있고, 서버 설정과 다르면 -> 로컬 설정을 믿고 서버를 업데이트한다.
    // (이유: 사용자가 방금 변경했는데 서버에 반영 안 된 경우를 방지)
    if (localLang && serverLang && localLang !== serverLang) {
      logger.warn(`⚠️ 언어 불일치 감지! 로컬(${localLang})을 우선 적용하고 서버를 동기화합니다.`);
      finalLanguage = localLang;
      
      // 백엔드에 다시 업데이트 요청 (비동기로 조용히 실행)
      this.updateProfile({ preferred_language: localLang }).catch(e => {
        logger.warn('⚠️ 백엔드 언어 동기화 실패 (무시됨):', e);
      });
      
    } else {
      // 그 외의 경우 (로컬이 없거나, 서버와 같음) -> 서버 데이터 또는 로컬 데이터 사용
      finalLanguage = serverLang || localLang || 'en';
    }

    // 3. 최종 언어 적용
    logger.debug(`✅ [AuthService] 최종 언어 적용: ${finalLanguage}`);
    i18nService.changeLanguage(finalLanguage);

    // 4. 로컬 스토리지 최신화
    if (typeof window !== 'undefined') {
      localStorage.setItem('dsk_language', finalLanguage);
    }
  }

  // =============================================================================
  // 🔥 토큰 검증 메서드
  // =============================================================================
  async verifyCurrentToken() {
    try {
      logger.debug('[AuthService] 토큰 검증 시작');
      const token = await getStoredToken();
      
      if (!token) {
        logger.debug('[AuthService] 저장된 토큰 없음');
        return { valid: false };
      }

      // ✅ [핵심] 검증 전에 apiClient에 토큰 설정
      setAuthToken(token);

      try {
        // ✅ [수정됨] 직접 경로 사용으로 오타 방지
        const user = await apiClient.get('/auth/me');
        
        if (!user || !user.id || !isValidUUID(user.id)) {
          logger.warn('[AuthService] 유효하지 않은 사용자 정보');
          return { valid: false };
        }

        logger.debug('[AuthService] 토큰 검증 성공:', user.email);
        
        // 🔥 언어 설정 적용
        this._applyUserLanguage(user);

        // 사용자 정보 저장
        await storeUserData(user);
        this.currentUser = user;
        this.isAuthenticated = true;

        // 🔥 authStore 업데이트
        if (this.authStore) {
          this.authStore.getState().setUser(user);
          this.authStore.getState().setToken(token);
        }

        return { valid: true, user };
      } catch (error) {
        if (error.response?.status === 401) {
          logger.debug('[AuthService] 토큰 만료 또는 무효');
          // ✅ 토큰이 무효하면 클리어
          clearAuthTokens();
        } else {
          logger.error('[AuthService] 토큰 검증 실패:', error);
        }
        return { valid: false };
      }
    } catch (error) {
      logger.error('[AuthService] 토큰 검증 중 예외:', error);
      return { valid: false };
    }
  }

  // =============================================================================
  // 일반 로그인
  // =============================================================================
  async login(email, password) {
    try {
      logger.debug('[AuthService] 로그인 시도:', email);
      
      // ✅ [수정됨] 직접 경로 사용으로 오타 방지 (/auuth -> /auth)
      const response = await apiClient.post('/auth/login', {
        email: email.toLowerCase().trim(),
        password,
        remember_me: true,
      });

      const { user, access_token, refresh_token, refreshToken } = response;
      // 백엔드에서 refresh_token 또는 refreshToken으로 올 수 있음
      const finalRefreshToken = refresh_token || refreshToken;

      if (!user || !access_token) {
        throw new Error('서버 응답이 올바르지 않습니다');
      }

      if (!isValidUUID(user.id)) {
        logger.warn('⚠️ 유효하지 않은 UUID:', user.id);
        throw new Error('서버에서 유효하지 않은 사용자 정보를 반환했습니다');
      }

      // ✅ [핵심] 로그인 성공 시 apiClient에 토큰 설정
      setAuthToken(access_token);
      if (finalRefreshToken) {
        setRefreshToken(finalRefreshToken);
      }

      // 🔥 언어 설정 적용 (데이터 저장보다 먼저 실행)
      this._applyUserLanguage(user);

      // 🔥 통합 인증 데이터 설정 (storage + store)
      await this.setAuthenticationData(user, access_token, finalRefreshToken);

      logger.debug('✅ [AuthService] 로그인 및 토큰 설정 완료');
      return { user, access_token, refreshToken: finalRefreshToken };
    } catch (error) {
      logger.error('❌ [AuthService] 로그인 실패:', error);
      throw this.handleApiError(error);
    }
  }

  // 📧 [2026-07-06] 이메일 OTP — 인증번호 발송
  async sendEmailOtp(email, lang = 'ko') {
    const response = await apiClient.post('/auth/email-otp/start', {
      email: (email || '').toLowerCase().trim(),
      lang,
    });
    return response; // { success, expires_in, dev_code? }
  }

  // 📧 [2026-07-06] 이메일 OTP — 인증번호 검증 → verification_token 반환
  async verifyEmailOtp(email, code) {
    const response = await apiClient.post('/auth/email-otp/verify', {
      email: (email || '').toLowerCase().trim(),
      code: (code || '').trim(),
    });
    return response; // { verified, verification_token, token_expires_in }
  }

  async register(userData) {
    try {
      logger.debug('📝 [AuthService] 회원가입 시작:', userData.email);
      
      // ✅ [수정됨] 직접 경로 사용으로 오타 방지
      const response = await apiClient.post('/auth/register', {
        ...userData,
        email: userData.email.toLowerCase().trim(),
      });

      const { user, access_token, refresh_token, refreshToken } = response;
      const finalRefreshToken = refresh_token || refreshToken;

      if (!user) {
        throw new Error('회원가입 응답이 올바르지 않습니다');
      }

      if (!isValidUUID(user.id)) {
        logger.warn('⚠️ 유효하지 않은 UUID:', user.id);
        throw new Error('서버에서 유효하지 않은 사용자 정보를 반환했습니다');
      }

      // ✅ [핵심] 토큰이 있으면 apiClient에 설정
      if (access_token) {
        setAuthToken(access_token);
        if (finalRefreshToken) {
          setRefreshToken(finalRefreshToken);
        }
        // 🔥 언어 설정 적용
        this._applyUserLanguage(user);
        await this.setAuthenticationData(user, access_token, finalRefreshToken);
        logger.debug('✅ [AuthService] 인증 정보 저장 완료');
      }

      logger.debug('✅ [AuthService] 회원가입 성공:', user.email);
      return {
        success: true,
        user,
        access_token,
        refreshToken: finalRefreshToken,
      };
    } catch (error) {
      logger.error('❌ [AuthService] 회원가입 실패:', error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        '회원가입 중 오류가 발생했습니다';
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  // =============================================================================
  // 게스트 로그인
  // =============================================================================
  async guestLogin() {
    try {
      logger.debug('[AuthService] 게스트 로그인 시도');
      
      const deviceId = await deviceManager.getDeviceId();
      const deviceInfo = await deviceManager.getDeviceInfo();

      if (!deviceId || deviceId.length < 10) {
        throw new Error('유효하지 않은 기기 ID입니다');
      }

      logger.debug('[AuthService] 기기 ID:', deviceId.substring(0, 8) + '...');

      // ✅ [수정됨] 직접 경로 사용으로 오타 방지
      const response = await apiClient.post('/auth/guest-login', {
        device_id: deviceId,
        device_info: deviceInfo,
        // 🔥 [추가] 현재 브라우저 언어를 서버로 전송 (첫 진입 시 자동 저장을 위해)
        preferred_language: i18nService.getLanguage()
      });

      const { user, access_token, refresh_token, refreshToken, is_guest } = response;
      const finalRefreshToken = refresh_token || refreshToken;

      if (!user || !access_token) {
        logger.error('[AuthService] 게스트 로그인 응답이 올바르지 않습니다:', response);
        throw new Error('게스트 로그인 응답이 올바르지 않습니다');
      }

      if (!isValidUUID(user.id)) {
        logger.warn('⚠️ 유효하지 않은 UUID:', user.id);
        throw new Error('서버에서 유효하지 않은 사용자 정보를 반환했습니다');
      }

      user.is_guest = is_guest || true;

      // ✅ [핵심] 게스트 로그인 성공 시 apiClient에 토큰 설정
      setAuthToken(access_token);
      if (finalRefreshToken) {
        setRefreshToken(finalRefreshToken);
      }

      // 🔥 언어 설정 적용
      this._applyUserLanguage(user);

      await this.setAuthenticationData(user, access_token, finalRefreshToken);

      logger.debug('✅ [AuthService] 게스트 로그인 성공:', user.id);
      return { user, access_token, refreshToken: finalRefreshToken, is_guest: true };
    } catch (error) {
      logger.error('❌ [AuthService] 게스트 로그인 실패:', error);
      throw this.handleApiError(error);
    }
  }

  async convertGuestToMember(userData) {
    try {
      logger.debug('🔄 [AuthService] 게스트 전환 시작:', userData.email);
      
      const currentUser = await getStoredUserData();
      if (!currentUser || !currentUser.is_guest) {
        throw new Error('게스트 사용자만 전환할 수 있습니다');
      }

      // ✅ [수정됨] 직접 경로 사용으로 오타 방지
      const response = await apiClient.post('/auth/convert-guest', {
        ...userData,
        email: userData.email.toLowerCase().trim(),
      });

      const { user, access_token, refresh_token, refreshToken } = response;
      const finalRefreshToken = refresh_token || refreshToken;

      if (!user || !access_token) {
        throw new Error('전환 응답이 올바르지 않습니다');
      }

      if (!isValidUUID(user.id)) {
        logger.warn('⚠️ 유효하지 않은 UUID:', user.id);
        throw new Error('서버에서 유효하지 않은 사용자 정보를 반환했습니다');
      }

      user.is_guest = false;

      // ✅ [핵심] 전환 성공 시 apiClient에 토큰 설정
      setAuthToken(access_token);
      if (finalRefreshToken) {
        setRefreshToken(finalRefreshToken);
      }

      // 🔥 언어 설정 적용
      this._applyUserLanguage(user);

      await this.setAuthenticationData(user, access_token, finalRefreshToken);

      logger.debug('✅ [AuthService] 게스트 전환 성공:', user.email);
      return {
        success: true,
        user,
        access_token,
        refreshToken: finalRefreshToken,
      };
    } catch (error) {
      logger.error('❌ [AuthService] 게스트 전환 실패:', error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        '게스트 전환 중 오류가 발생했습니다';
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  async isGuest() {
    try {
      const user = await getStoredUserData();
      return user?.is_guest === true || user?.user_type === 'GUEST';
    } catch (error) {
      logger.error('[AuthService] 게스트 확인 실패:', error);
      return false;
    }
  }

  async getGuestExpirationInfo() {
    try {
      const user = await getStoredUserData();
      if (!user || !user.is_guest) {
        return null;
      }
      return {
        expires_at: user.guest_expires_at,
        is_expired: user.is_guest_expired,
        days_remaining: user.days_until_guest_expiration,
      };
    } catch (error) {
      logger.error('[AuthService] 게스트 만료 정보 확인 실패:', error);
      return null;
    }
  }

  // =============================================================================
  // 🔥 소셜 로그인 (네이티브 앱용)
  // =============================================================================
  async socialLogin(provider) {
    try {
      logger.debug(`[AuthService] ${provider} 소셜 로그인 시도`);
      
      const socialData = await socialAuthService.signInWithProvider(provider);
      logger.debug(`✅ ${provider} 인증 성공, 백엔드로 전송 중...`);

      const response = await this.sendSocialLoginToBackend(provider, socialData);

      const { user, access_token, refresh_token, refreshToken } = response;
      const finalRefreshToken = refresh_token || refreshToken;

      if (!user || !access_token) {
        throw new Error('소셜 로그인 응답이 올바르지 않습니다');
      }

      if (!isValidUUID(user.id)) {
        logger.warn('⚠️ 유효하지 않은 UUID:', user.id);
        throw new Error('서버에서 유효하지 않은 사용자 정보를 반환했습니다');
      }

      // ✅ [핵심] 소셜 로그인 성공 시 apiClient에 토큰 설정
      setAuthToken(access_token);
      if (finalRefreshToken) {
        setRefreshToken(finalRefreshToken);
      }

      // 🔥 언어 설정 적용
      this._applyUserLanguage(user);

      await this.setAuthenticationData(user, access_token, finalRefreshToken);

      logger.debug(`✅ ${provider} 소셜 로그인 완료`);
      return { user, access_token, refreshToken: finalRefreshToken };
    } catch (error) {
      logger.error(`❌ ${provider} 소셜 로그인 실패:`, error);
      if (error.message.includes('취소')) {
        throw error;
      }
      throw this.handleApiError(error);
    }
  }

  async sendSocialLoginToBackend(provider, socialData) {
    let endpoint;
    let payload;

    switch (provider.toLowerCase()) {
      case 'google':
        endpoint = '/auth/social/google';
        payload = {
          access_token: socialData.accessToken,
          user_type: 'VISITOR',
        };
        break;
      case 'apple':
        endpoint = '/auth/social/apple';
        payload = {
          identity_token: socialData.identityToken,
          authorization_code: socialData.authorizationCode,
          user_type: 'VISITOR',
        };
        break;
      case 'kakao':
        endpoint = '/auth/social/kakao';
        payload = {
          code: socialData.accessToken,
          state: 'kakao_login',
          user_type: 'VISITOR',
        };
        break;
      case 'naver':
        endpoint = '/auth/social/naver';
        payload = {
          code: socialData.accessToken,
          state: 'naver_login',
          user_type: 'VISITOR',
        };
        break;
      default:
        throw new Error(`지원하지 않는 소셜 로그인: ${provider}`);
    }

    logger.debug(`📤 Sending ${provider} data to backend:`, endpoint);
    const response = await apiClient.post(endpoint, payload);
    return response;
  }

  // =============================================================================
  // 🔥 소셜 로그인 (웹용 - OAuth 리다이렉트 방식)
  // =============================================================================
  /**
   * 소셜 로그인 URL 가져오기 (웹용)
   * @param {string} provider - 소셜 로그인 제공자 (google, kakao, naver, apple)
   * @returns {Promise<string>} 로그인 URL
   */
  async getSocialLoginUrl(provider) {
    try {
      const response = await apiClient.get(`/auth/social/${provider}/url`);
      return response.data?.url || response.url;
    } catch (error) {
      logger.error('소셜 로그인 URL 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 소셜 로그인 콜백 처리 (웹용)
   * @param {string} provider - 소셜 로그인 제공자
   * @param {string} code - 인증 코드
   * @returns {Promise<Object>} 로그인 결과
   */
  async handleSocialCallback(provider, code) {
    try {
      const response = await apiClient.post(`/auth/social/${provider}/callback`, { code });
      
      const { access_token, user, refresh_token, refreshToken } = response.data || response;
      const finalRefreshToken = refresh_token || refreshToken;

      if (!user || !access_token) {
        throw new Error('소셜 로그인 콜백 응답이 올바르지 않습니다');
      }

      if (!isValidUUID(user.id)) {
        logger.warn('⚠️ 유효하지 않은 UUID:', user.id);
        throw new Error('서버에서 유효하지 않은 사용자 정보를 반환했습니다');
      }

      // ✅ [핵심] 소셜 콜백 성공 시 apiClient에 토큰 설정
      setAuthToken(access_token);
      if (finalRefreshToken) {
        setRefreshToken(finalRefreshToken);
      }

      // 🔥 언어 설정 적용
      this._applyUserLanguage(user);

      await this.setAuthenticationData(user, access_token, finalRefreshToken);

      return { user, token: access_token };
    } catch (error) {
      logger.error('소셜 로그인 콜백 처리 실패:', error);
      throw this.handleApiError(error);
    }
  }

  // =============================================================================
  // 로그아웃
  // =============================================================================
  async logout() {
    try {
      const token = await getStoredToken();
      if (token && apiClient) {
        // ✅ [수정됨] 직접 경로 사용
        await apiClient.post('/auth/logout').catch(() => {});
        logger.debug('[AuthService] 로그아웃 성공');
      }
    } catch (error) {
      logger.warn('[AuthService] 로그아웃 실패:', error);
    } finally {
      // ✅ [핵심] 클라이언트 데이터 클리어
      await this.clearAuthenticationData();
      logger.debug('[AuthService] 로그아웃 완료');
    }
  }

  // =============================================================================
  // 토큰 관리
  // =============================================================================
  async refreshToken() {
    try {
      // ✅ [수정됨] 직접 경로 사용
      const response = await apiClient.post('/auth/refresh');
      const { access_token, refresh_token, refreshToken } = response;
      const finalRefreshToken = refresh_token || refreshToken;

      await storeToken(access_token);
      if (finalRefreshToken) {
        await storeToken(finalRefreshToken, 'refresh_token');
      }

      // ✅ [핵심] apiClient에 새 토큰 설정
      setAuthToken(access_token);
      if (finalRefreshToken) {
        setRefreshToken(finalRefreshToken);
      }

      // 🔥 authStore 업데이트
      if (this.authStore) {
        this.authStore.getState().setToken(access_token);
      }

      logger.debug('✅ [AuthService] 토큰 갱신 완료');
      return { access_token, refreshToken: finalRefreshToken };
    } catch (error) {
      logger.error('❌ [AuthService] 토큰 갱신 실패:', error);
      await this.clearAuthenticationData();
      throw this.handleApiError(error);
    }
  }

  // =============================================================================
  // 현재 사용자 정보
  // =============================================================================
  /**
   * 현재 사용자 정보 조회 (내 프로필)
   * @returns {Promise<Object>} 사용자 정보
   */
  async getCurrentUser() {
    try {
      const response = await apiClient.get('/auth/me');
      const user = response.data || response;
      if (user && isValidUUID(user.id)) {
        // 🔥 언어 설정 적용
        this._applyUserLanguage(user);
        await storeUserData(user);
        this.currentUser = user;
        // 🔥 authStore 업데이트
        if (this.authStore) {
          this.authStore.getState().setUser(user);
        }
      }
      return user;
    } catch (error) {
      logger.error('사용자 정보 조회 실패:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * 내 프로필 조회 (alias for getCurrentUser)
   * @returns {Promise<Object>} 사용자 정보
   */
  async getProfile() {
    return await this.getCurrentUser();
  }

  // =============================================================================
  // 🔥 [보완 수정] 프로필 업데이트 (필드명 호환성 추가)
  // =============================================================================
  
  /**
   * 프로필 업데이트 (DB 저장 + 스토어 동기화)
   * @param {Object} profileData - 업데이트할 프로필 정보
   * @returns {Promise<Object>} 업데이트된 사용자 정보
   */
  async updateProfile(profileData) {
    try {
      logger.debug('📝 [AuthService] 프로필 업데이트 시작:', Object.keys(profileData));
      
      // 🔥 [보완] 백엔드 스키마가 preferred_language 인지 language 인지 불확실할 때
      // 두 필드를 모두 보내서 확실하게 저장되도록 함
      const payload = { ...profileData };
      if (profileData.preferred_language) {
        payload.language = profileData.preferred_language; // 혹시 모를 필드명 대응
      }

      // 1. 백엔드 업데이트 요청
      const response = await apiClient.put('/auth/me', payload);
      const updatedUser = response.data || response;

      // 2. 로컬 스토리지 업데이트
      await storeUserData(updatedUser);
      this.currentUser = updatedUser;

      // 3. Zustand 스토어 업데이트
      if (this.authStore) {
        // 만약 언어를 바꿨는데 서버 응답에는 반영이 안 되어 있다면? -> 요청한 값으로 강제 덮어쓰기
        if (profileData.preferred_language && updatedUser.preferred_language !== profileData.preferred_language) {
          logger.warn('⚠️ 서버 응답에 언어 변경이 반영되지 않음. 클라이언트 상태 강제 업데이트.');
          updatedUser.preferred_language = profileData.preferred_language;
        }
        this.authStore.getState().updateUser(updatedUser);
      }

      logger.debug('✅ [AuthService] 프로필 업데이트 완료');
      return updatedUser;
    } catch (error) {
      logger.error('❌ [AuthService] 프로필 업데이트 실패:', error);
      throw this.handleApiError(error);
    }
  }

  // =============================================================================
  // 🔥 [핵심 수정] 프로필 이미지 업로드 - 전용 엔드포인트 사용
  // =============================================================================
  /**
   * 프로필 이미지 업로드 및 적용
   * @param {File} imageFile - 이미지 파일
   * @returns {Promise<Object>} 업로드 결과 { success, image_url, user }
   */
  async uploadProfileImage(imageFile) {
    try {
      const formData = new FormData();
      formData.append('file', imageFile); // 🔥 주의: 백엔드 파라미터명이 'file'임

      logger.debug('📤 [AuthService] 프로필 전용 업로드 시작 (/api/users/me/profile-image)...');

      // 1. 🔥 [수정] 전용 엔드포인트 호출
      // 이 API는 이미지를 'profiles' 폴더에 저장하고, DB의 user.profile_image도 업데이트함
      const response = await apiClient.post('/users/me/profile-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data || response;
      const imageUrl = result.image_url;

      if (!imageUrl) {
        throw new Error('이미지 업로드 후 URL을 받지 못했습니다.');
      }

      logger.debug('✅ [AuthService] 업로드 및 DB 저장 완료 URL:', imageUrl);

      // 2. [최적화] 백엔드에서 이미 DB를 업데이트했으므로, 
      // 프론트엔드 스토어(authStore)만 최신화하면 됨 (별도의 updateProfile 호출 불필요)
      
      // 현재 내 정보(DB 최신값)를 다시 불러와서 스토어 동기화 (가장 안전)
      const updatedUser = await this.getCurrentUser();

      return { 
        success: true, 
        image_url: imageUrl,
        user: updatedUser
      };
    } catch (error) {
      logger.error('❌ [AuthService] 프로필 이미지 업로드 실패:', error);
      throw this.handleApiError(error);
    }
  }

  // =============================================================================
  // 🔥🔥🔥 [2026-01-15 수정] 비밀번호 및 계정 정보 변경 - 전화번호 + SMS 동의 필드 지원
  // =============================================================================
  /**
   * 비밀번호 변경 및 계정 정보 업데이트
   * @param {Object} data - 변경할 정보
   * @param {string} data.currentPassword - 현재 비밀번호
   * @param {string} data.newPassword - 새 비밀번호
   * @param {string} [data.newEmail] - 새 이메일 (선택)
   * @param {string} [data.newName] - 새 이름 (선택)
   * @param {string} [data.newPhoneNumber] - 새 전화번호 (선택) 🔥 추가
   * @param {string} [data.phoneVerificationToken] - 전화번호 인증 토큰 (선택) 🔥 추가
   * @param {boolean} [data.smsConsent] - SMS 수신 동의 (선택) 🔥 추가
   */
  async changePassword(data) {
    try {
      // 🔥 [수정] 파라미터를 객체로 받고, 백엔드 스키마(snake_case)로 매핑
      const payload = {
        current_password: data.currentPassword || data.current_password,
        new_password: data.newPassword || data.new_password,
        // 선택적 필드 추가
        new_email: data.newEmail || data.new_email || null,
        new_name: data.newName || data.new_name || null,
        // 🔥🔥🔥 [신규] 전화번호 관련 필드 추가
        new_phone_number: data.newPhoneNumber || data.new_phone_number || null,
        phone_verification_token: data.phoneVerificationToken || data.phone_verification_token || null,
        // 🔥🔥🔥 [신규] SMS 수신 동의 필드 추가
        sms_consent: data.smsConsent !== undefined ? data.smsConsent : (data.sms_consent !== undefined ? data.sms_consent : null)
      };

      // 유효성 검사 (최소한의 방어)
      if (!payload.current_password || !payload.new_password) {
        throw new Error('현재 비밀번호와 새 비밀번호는 필수입니다.');
      }

      logger.debug('🔐 [AuthService] 비밀번호 변경 요청:', {
        hasEmail: !!payload.new_email,
        hasName: !!payload.new_name,
        hasPhone: !!payload.new_phone_number,
        hasPhoneToken: !!payload.phone_verification_token,
        smsConsent: payload.sms_consent
      });

      const result = await apiClient.post('/auth/change-password', payload);
      
      // 🔥 응답에서 업데이트된 사용자 정보가 있으면 스토어 업데이트
      const responseData = result.data || result;
      if (responseData.user && this.authStore) {
        this.authStore.getState().updateUser(responseData.user);
        logger.debug('✅ [AuthService] 사용자 정보 스토어 업데이트 완료');
      }
      
      return responseData;
    } catch (error) {
      logger.error('비밀번호 변경 실패:', error);
      throw this.handleApiError(error);
    }
  }

  async forgotPassword(email) {
    try {
      // ✅ [수정됨] 직접 경로 사용
      const result = await apiClient.post('/auth/reset-password', {
        email: email.toLowerCase().trim(),
      });
      return result;
    } catch (error) {
      throw this.handleApiError(error);
    }
  }

  // =============================================================================
  // 🔥 비밀번호 재설정 (Redis 기반)
  // =============================================================================
  /**
   * 비밀번호 재설정 요청 (이메일 발송)
   * @param {string} email - 이메일 주소
   * @returns {Promise<Object>} 결과
   */
  async requestPasswordReset(email) {
    try {
      const response = await apiClient.post('/auth/password-reset/request', {
        email: email.toLowerCase().trim(),
      });
      return response.data || response;
    } catch (error) {
      logger.error('비밀번호 재설정 요청 실패:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * 비밀번호 재설정 토큰 유효성 확인
   * @param {string} token - 재설정 토큰
   * @returns {Promise<Object>} { valid: boolean, expires_in: number, message: string }
   */
  async verifyResetToken(token) {
    try {
      const response = await apiClient.get(`/auth/password-reset/verify/${token}`);
      return response.data || response;
    } catch (error) {
      logger.error('토큰 검증 실패:', error);
      // 에러 응답에서 메시지 추출
      const errorMessage = 
        error.response?.data?.detail || 
        error.response?.data?.message ||
        error.message ||
        '토큰 검증에 실패했습니다.';
      
      return {
        valid: false,
        message: errorMessage
      };
    }
  }

  /**
   * 비밀번호 재설정 (토큰 사용)
   * @param {string} token - 재설정 토큰
   * @param {string} newPassword - 새 비밀번호
   * @returns {Promise<Object>} 결과
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await apiClient.post('/auth/password-reset/confirm', {
        token,
        new_password: newPassword,
      });
      return response.data || response;
    } catch (error) {
      logger.error('비밀번호 재설정 실패:', error);
      throw this.handleApiError(error);
    }
  }

  // =============================================================================
  // 🔥 이메일 인증
  // =============================================================================
  /**
   * 이메일 인증 요청
   * @returns {Promise<Object>} 결과
   */
  async requestEmailVerification() {
    try {
      const response = await apiClient.post('/auth/verify-email/request');
      return response.data || response;
    } catch (error) {
      logger.error('이메일 인증 요청 실패:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * 이메일 인증 확인
   * @param {string} token - 인증 토큰
   * @returns {Promise<Object>} 결과
   */
  async verifyEmail(token) {
    try {
      const response = await apiClient.post('/auth/verify-email/confirm', { token });
      return response.data || response;
    } catch (error) {
      logger.error('이메일 인증 확인 실패:', error);
      throw this.handleApiError(error);
    }
  }

  // =============================================================================
  // 🔥 알림 설정 관련
  // =============================================================================
  /**
   * 알림 설정 조회
   * @returns {Promise<Object>} 알림 설정
   */
  async getNotificationSettings() {
    try {
      const response = await apiClient.get('/auth/notification-settings');
      return response.data || response;
    } catch (error) {
      logger.error('알림 설정 조회 실패:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * 알림 설정 업데이트
   * @param {Object} settings - 알림 설정 객체
   * @returns {Promise<Object>} 업데이트된 설정
   */
  async updateNotificationSettings(settings) {
    try {
      const response = await apiClient.put('/auth/notification-settings', settings);
      return response.data || response;
    } catch (error) {
      logger.error('알림 설정 업데이트 실패:', error);
      throw this.handleApiError(error);
    }
  }

  // =============================================================================
  // 🔥 계정 삭제
  // =============================================================================
  /**
   * 계정 삭제
   * @param {string} password - 현재 비밀번호 (확인용)
   * @returns {Promise<Object>} 결과
   */
  async deleteAccount(password) {
    try {
      const response = await apiClient.delete('/auth/account', {
        data: { password },
      });

      // 로컬 데이터 정리
      await this.clearAuthenticationData();
      localStorage.clear();
      sessionStorage.clear();

      return response.data || response;
    } catch (error) {
      logger.error('계정 삭제 실패:', error);
      throw this.handleApiError(error);
    }
  }

  // =============================================================================
  // 🔥 통합 인증 데이터 관리
  // =============================================================================
  async setAuthenticationData(user, token, refreshTokenValue) {
    // 1. localStorage에 저장
    await storeToken(token);
    await storeUserData(user);
    if (refreshTokenValue) {
      await storeToken(refreshTokenValue, 'refresh_token');
    }

    // 2. ✅ [핵심] apiClient에 토큰 설정 (setAuthToken 함수 사용)
    setAuthToken(token);
    if (refreshTokenValue) {
      setRefreshToken(refreshTokenValue);
    }

    // 3. 로컬 상태 업데이트
    this.currentUser = user;
    this.isAuthenticated = true;

    // 4. 🔥 authStore 업데이트
    if (this.authStore) {
      this.authStore.getState().setUser(user);
      this.authStore.getState().setToken(token);
      logger.debug('✅ [AuthService] authStore 상태 업데이트 완료');
    }
  }

  async clearAuthenticationData() {
    // 1. localStorage에서 제거
    await removeStoredToken();
    await removeUserData();
    await removeStoredToken('refresh_token');

    // 2. ✅ [핵심] apiClient 토큰 클리어 (clearAuthTokens 함수 사용)
    clearAuthTokens();

    // 3. 로컬 상태 초기화
    this.currentUser = null;
    this.isAuthenticated = false;

    // 4. 🔥 authStore 클리어
    if (this.authStore) {
      await this.authStore.getState().logout();
      logger.debug('✅ [AuthService] authStore 로그아웃 완료');
    }
  }

  // =============================================================================
  // 에러 핸들링
  // =============================================================================
  handleApiError(error) {
    let errorMessage = '알 수 없는 오류가 발생했습니다';

    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      switch (status) {
        case 400:
          errorMessage = data.detail || data.message || '잘못된 요청입니다';
          break;
        case 401:
          errorMessage = '이메일 또는 비밀번호가 올바르지 않습니다';
          break;
        case 403:
          errorMessage = '접근이 거부되었습니다';
          break;
        case 404:
          errorMessage = '요청한 리소스를 찾을 수 없습니다';
          break;
        case 422:
          errorMessage = data.detail?.[0]?.msg || '입력 데이터가 올바르지 않습니다';
          break;
        case 429:
          // 🔥 [추가] 재요청 제한 에러 처리
          errorMessage = data.detail || '잠시 후 다시 시도해주세요';
          break;
        case 500:
          errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
          break;
        default:
          errorMessage = `서버 오류 (${status}): ${data.detail || data.message || '알 수 없는 오류'}`;
      }
    } else if (error.request) {
      errorMessage = '네트워크 연결을 확인해주세요';
    } else {
      errorMessage = error.message || '요청 처리 중 오류가 발생했습니다';
    }

    return new Error(errorMessage);
  }

  // =============================================================================
  // 🔥 이메일 중복 체크
  // =============================================================================
  async checkEmailExists(email) {
    const response = await apiClient.get(`/auth/check-email-exists?email=${encodeURIComponent(email)}`);
    return response.data || response;
  }

  // =============================================================================
  // 🔥 SMS 인증 기반 비밀번호 재설정 토큰 직접 발급
  // =============================================================================
  async getDirectResetToken(email, phoneNumber, smsToken) {
    const response = await apiClient.post('/auth/password-reset/direct-token', {
      email: email.toLowerCase().trim(),
      phone_number: phoneNumber,
      phone_verification_token: smsToken
    });
    return response.data || response;
  }

  // =============================================================================
  // 유틸리티
  // =============================================================================
  getServiceInfo() {
    return {
      serviceName: 'AuthService',
      environment: import.meta.env.VITE_ENVIRONMENT || 'development',
      apiEnabled: true,
      guestLoginEnabled: true,
      socialLoginEnabled: true,
    };
  }
}

const authService = new AuthService();

// 개발 환경에서 정보 출력
if (import.meta.env.DEV) {
  setTimeout(() => {
    logger.debug('✅ AuthService loaded:', authService.getServiceInfo());
  }, 1000);
}

// 싱글톤으로 export (named export 추가)
export { authService };
export default authService;
